import React, { useState, useEffect } from 'react'
import {
  Shield,
  ArrowLeft,
  Calendar,
  Mail,
  Building,
  AlertTriangle,
  CheckCircle,
  Scale
} from 'lucide-react'
import { SystemSettings, PageContent } from '../lib/storage'
import PageContentRenderer from '../components/PageContentRenderer'

const DmcaCounterPage = () => {
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null)
  const [heroContent, setHeroContent] = useState<any>(null)
  const [pageContent, setPageContent] = useState<PageContent | null>(null)
  const [loading, setLoading] = useState(true)

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)

        // Load all data concurrently using Promise.allSettled
        const results = await Promise.allSettled([
          fetch('/api/settings-db'),
          fetch('/api/hero'),
          fetch('/api/admin-page-content')
        ])

        // Process settings
        if (results[0].status === 'fulfilled') {
          const settingsResponse = results[0].value
          if (!settingsResponse.ok) {
            console.error(`[DmcaCounter] Settings API returned ${settingsResponse.status}: ${settingsResponse.statusText}`)
          } else {
            const settingsData = await settingsResponse.json()
            const settings = settingsData.success ? settingsData.result : settingsData
            if (settings && settings.id) {
              setSystemSettings(settings)
            }
          }
        } else {
          console.error('[DmcaCounter] Settings fetch failed:', results[0].reason)
        }

        // Process hero content
        if (results[1].status === 'fulfilled') {
          const heroResponse = results[1].value
          if (!heroResponse.ok) {
            console.error(`[DmcaCounter] Hero API returned ${heroResponse.status}: ${heroResponse.statusText}`)
          } else {
            const heroData = await heroResponse.json()
            const hero = heroData.success ? heroData.result : heroData
            setHeroContent(hero?.dmcaCounter || null)
          }
        } else {
          console.error('[DmcaCounter] Hero fetch failed:', results[1].reason)
        }

        // Process page content
        if (results[2].status === 'fulfilled') {
          const pageResponse = results[2].value
          if (!pageResponse.ok) {
            console.error(`[DmcaCounter] Page content API returned ${pageResponse.status}: ${pageResponse.statusText}`)
          } else {
            const pagesData = await pageResponse.json()
            const dmcaCounterPage = pagesData.find((p: PageContent) => p.pageId === 'dmca-counter')
            if (dmcaCounterPage) {
              setPageContent(dmcaCounterPage)
            }
          }
        } else {
          console.error('[DmcaCounter] Page content fetch failed:', results[2].reason)
        }
      } catch (err) {
        console.error('Error loading page data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dynamic Theme Styles */}
      {systemSettings && (
        <style>
          {`
            :root {
              --primary-500: ${systemSettings.primaryColor || '#2E51ED'};
              --netlify-500: ${systemSettings.primaryColor || '#2E51ED'};
              --secondary-color: ${systemSettings.secondaryColor || '#05BDBA'};
            }
          `}
        </style>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                {systemSettings?.logoUrl ? (
                  <img src={systemSettings.logoUrl} alt="Logo" className="w-8 h-8 rounded" />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-800 rounded flex items-center justify-center">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {systemSettings?.brandName || systemSettings?.companyName || 'Netlify'}
                  </h1>
                  <p className="text-sm text-gray-600 -mt-1">Trust Center</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/" className="text-primary-600 hover:text-primary-700 inline-flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Trust Center
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">
              {heroContent?.title || 'DMCA Counter Notice Guide'}
            </h1>
            {heroContent?.subtitle && (
              <h2 className="text-2xl text-primary-200 mb-4">
                {heroContent.subtitle}
              </h2>
            )}
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              {heroContent?.description ||
               'Guide for submitting a DMCA counter notice if you believe your content was mistakenly disabled.'}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 text-sm">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-primary-300 mr-2" />
                <span>Last Updated: November 1st, 2023</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Section */}
      <section className="py-8 bg-yellow-50 border-b border-yellow-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Legal Notice</h3>
              <p className="text-yellow-700">
                Filing a DMCA counter notice is a serious legal action. False counter notices can result in legal consequences.
                It is strongly recommended to consult with a qualified attorney before proceeding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {pageContent ? (
            <PageContentRenderer content={pageContent} systemSettings={systemSettings} />
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading content...</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-primary-400" />
              <span className="ml-2 text-lg font-semibold">Trust Center</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your security is our priority. Access comprehensive documentation
              and evidence of our security practices.
            </p>
            <p className="text-gray-400 text-sm">
              &copy; 2024 {systemSettings?.companyName || 'Company Name'}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default DmcaCounterPage